/* eslint-disable no-restricted-syntax -- the one place a raw <img> is correct. */
import { useState } from "react";

/**
 * Every image on the site renders through this component.
 *
 * It never touches the original file: the backend `/api/img` endpoint writes AVIF and WebP
 * derivatives beside it and this component asks for the width the layout actually needs, so a
 * phone is never served a desktop original. Any future image on the approved hosts, or any new
 * mirrored/CMS asset, is optimised automatically with no per-image work.
 *
 * If a derivative cannot be produced the component falls back to the original URL, so an image can
 * never disappear because of the pipeline.
 */

// Same convention as lib/api.js: production is served from the same origin as the API, so the
// preview hostname must never be baked into an image URL.
const API =
  process.env.NODE_ENV === "production" ? "" : process.env.REACT_APP_BACKEND_URL;

// Must stay in sync with ALLOWED_WIDTHS in backend/image_pipeline.py.
const WIDTHS = [320, 480, 640, 768, 1024, 1280, 1600, 1920];

const HOSTS = new Set([
  "static.prod-images.emergentagent.com",
  "customer-assets-gfyr7b9c.emergentagent.net",
  "images.pexels.com",
  "images.unsplash.com",
]);

function optimisable(src) {
  if (!src || typeof src !== "string") return false;
  if (src.startsWith("/api/assets/")) return true;
  try {
    const u = new URL(src, window.location.origin);
    return u.pathname.startsWith("/api/assets/") || HOSTS.has(u.host);
  } catch {
    return false;
  }
}

const derivative = (src, w, f) =>
  `${API}/api/img?src=${encodeURIComponent(src)}&w=${w}&f=${f}`;

export const SmartImage = ({
  src,
  alt = "",
  sizes = "100vw",
  priority = false,
  maxWidth = 1920,
  className,
  style,
  width,
  height,
  onError,
  ...rest
}) => {
  const [rawFallback, setRawFallback] = useState(false);
  const optimised = !rawFallback && optimisable(src);

  const candidates = WIDTHS.filter((w) => w <= maxWidth);
  if (!candidates.length) candidates.push(WIDTHS[0]);
  const srcSet = (f) => candidates.map((w) => `${derivative(src, w, f)} ${w}w`).join(", ");
  const fallbackWidth = Math.min(1280, candidates[candidates.length - 1]);

  const img = (
    <img
      src={optimised ? derivative(src, fallbackWidth, "webp") : src}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      {...(priority ? { fetchPriority: "high" } : {})}
      width={width}
      height={height}
      className={className}
      style={style}
      onError={(e) => {
        if (optimised) setRawFallback(true);
        if (onError) onError(e);
      }}
      {...rest}
    />
  );

  if (!optimised) return img;

  return (
    // `contents` keeps the <img> in the parent's layout box, so existing absolute/h-full
    // positioning behaves exactly as it did before the picture element was introduced.
    <picture className="contents">
      <source type="image/avif" srcSet={srcSet("avif")} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet("webp")} sizes={sizes} />
      {img}
    </picture>
  );
};
